/**
 * Shared candidate pipeline mutations used by Table, Meta CV, and Kanban.
 * Owns confirmation / Calendly dialog state; surfaces only open the flows.
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import type { Candidate, JobOffer, PipelineStage } from '../models/job';
import type { ExamConfig } from '../types/job.types';
import {
  acceptCandidate,
  rejectCandidate,
  hireCandidate,
} from '../services/job.service';
import type { AcceptCandidatePayload } from '../utils/bookingLink';
import { useSelectCandidatesForInterview, useJobPipeline } from './useJobOffers';
import { useCandidateEmail } from './useCandidateEmail';
import { isMissingEmail } from '../utils/candidate-email';
import { useGuardedAction } from './useGuardedAction';
import { mapDragToPipelineAction } from '../utils/candidate-actions';
import {
  applyActionFeedback,
  feedbackErrorMessage,
  feedbackForSendInterview,
  feedbackForStatusChange,
  isInterviewInvitationAlreadySent,
  isStatusActionAlreadyApplied,
  type ActionFeedback,
  type StatusAction,
} from '../utils/pipeline-action-feedback';
import { findStageByType, stageWillNotify } from '../utils/pipeline-stages';

const TOAST_PREFIX = 'candidates:notifications';

export interface UseCandidatePipelineMutationsArgs {
  job: JobOffer;
  examConfig: ExamConfig;
  requiresReview?: boolean;
  onStatusUpdate?: () => void;
  onDeleteCandidate?: (candidateId: string, candidateName: string) => void;
  onExtendInterview?: (
    interviewId: string,
    candidateName: string,
    currentExpiration: string | null
  ) => void;
}

export function useCandidatePipelineMutations({
  job,
  examConfig,
  requiresReview = false,
  onStatusUpdate,
  onDeleteCandidate,
  onExtendInterview,
}: UseCandidatePipelineMutationsArgs) {
  const { t } = useTranslation(['candidates']);
  const { getAccessTokenSilently } = useAuth0();
  const selectCandidatesMutation = useSelectCandidatesForInterview(job.id);
  const { data: pipeline } = useJobPipeline(job.id);
  const { updateEmail, isUpdating: isUpdatingEmail } = useCandidateEmail({
    jobId: job.id,
  });
  const { guardedAction: guardInterviewAction } = useGuardedAction(
    'interview',
    1,
    'candidate_actions_button'
  );

  const [activeCandidate, setActiveCandidate] = useState<Candidate | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isHiring, setIsHiring] = useState(false);
  const [isSendingInterview, setIsSendingInterview] = useState(false);
  const [showSendInterviewDialog, setShowSendInterviewDialog] = useState(false);
  const [pendingEmailForSend, setPendingEmailForSend] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showHireDialog, setShowHireDialog] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  // Per-move "send email" checkbox; re-checked every time a dialog opens.
  const [sendNotification, setSendNotification] = useState(true);

  const closeAccept = useCallback(() => {
    setShowAcceptDialog(false);
  }, []);

  const notify = (feedback: ActionFeedback) => {
    applyActionFeedback(t, TOAST_PREFIX, feedback);
  };

  const notifyIfAlreadyApplied = (action: StatusAction, semantic?: string) => {
    if (!isStatusActionAlreadyApplied(action, semantic)) return false;
    notify(feedbackForStatusChange({ action, skipped: true }));
    return true;
  };

  const handleAccept = useCallback(
    async (candidate: Candidate, payload?: AcceptCandidatePayload) => {
      if (isAccepting) return;
      if (notifyIfAlreadyApplied('accept', candidate.pipelineStageSemantic)) {
        closeAccept();
        return true;
      }
      setIsAccepting(true);
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await acceptCandidate(
          accessToken,
          candidate.id,
          payload && { ...payload, send_notification: sendNotification }
        );
        notify(
          feedbackForStatusChange({
            action: 'accept',
            error: response.error,
          })
        );
        if (response.error) {
          return false;
        }
        closeAccept();
        onStatusUpdate?.();
        return true;
      } catch (error) {
        console.error('Error accepting candidate:', error);
        notify(feedbackForStatusChange({ action: 'accept', error: 'failed' }));
        return false;
      } finally {
        setIsAccepting(false);
      }
    },
    [isAccepting, sendNotification, getAccessTokenSilently, t, closeAccept, onStatusUpdate]
  );

  const openAccept = useCallback((candidate: Candidate) => {
    setActiveCandidate(candidate);
    setSendNotification(true);
    setShowAcceptDialog(true);
  }, []);

  const openReject = useCallback((candidate: Candidate) => {
    setActiveCandidate(candidate);
    setSendNotification(true);
    setShowRejectDialog(true);
  }, []);

  const confirmReject = useCallback(async () => {
    if (!activeCandidate || isRejecting) return false;
    setShowRejectDialog(false);
    if (notifyIfAlreadyApplied('reject', activeCandidate.pipelineStageSemantic)) {
      return true;
    }
    setIsRejecting(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await rejectCandidate(
        accessToken,
        activeCandidate.id,
        sendNotification
      );
      notify(
        feedbackForStatusChange({
          action: 'reject',
          error: response.error,
        })
      );
      if (response.error) {
        return false;
      }
      onStatusUpdate?.();
      return true;
    } catch (error) {
      console.error('Error rejecting candidate:', error);
      notify(feedbackForStatusChange({ action: 'reject', error: 'failed' }));
      return false;
    } finally {
      setIsRejecting(false);
    }
  }, [activeCandidate, isRejecting, sendNotification, getAccessTokenSilently, t, onStatusUpdate]);

  const openHire = useCallback((candidate: Candidate) => {
    setActiveCandidate(candidate);
    setShowHireDialog(true);
  }, []);

  const confirmHire = useCallback(async () => {
    if (!activeCandidate || isHiring) return false;
    setShowHireDialog(false);
    if (notifyIfAlreadyApplied('hire', activeCandidate.pipelineStageSemantic)) {
      return true;
    }
    setIsHiring(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await hireCandidate(accessToken, activeCandidate.id);
      notify(
        feedbackForStatusChange({
          action: 'hire',
          error: response.error,
        })
      );
      if (response.error) {
        return false;
      }
      onStatusUpdate?.();
      return true;
    } catch (error) {
      console.error('Error hiring candidate:', error);
      notify(feedbackForStatusChange({ action: 'hire', error: 'failed' }));
      return false;
    } finally {
      setIsHiring(false);
    }
  }, [activeCandidate, isHiring, getAccessTokenSilently, t, onStatusUpdate]);

  const openSendInterview = useCallback(
    (candidate: Candidate) => {
      if (isSendingInterview) return;
      if (requiresReview) {
        toast.error(t('candidates:notifications.job_incomplete'));
        return;
      }
      setActiveCandidate(candidate);
      setPendingEmailForSend('');
      setSendNotification(true);
      guardInterviewAction(() => {
        setShowSendInterviewDialog(true);
      })();
    },
    [isSendingInterview, requiresReview, t, guardInterviewAction]
  );

  const closeSendInterviewDialog = useCallback(() => {
    setShowSendInterviewDialog(false);
    setPendingEmailForSend('');
  }, []);

  const confirmSendInterview = useCallback(async () => {
    if (!activeCandidate) return false;
    setShowSendInterviewDialog(false);
    if (isInterviewInvitationAlreadySent(activeCandidate)) {
      notify(feedbackForSendInterview({ skipped: true }));
      return true;
    }
    setIsSendingInterview(true);
    try {
      if (isMissingEmail(activeCandidate) && pendingEmailForSend.trim()) {
        await updateEmail({
          candidateId: activeCandidate.id,
          email: pendingEmailForSend.trim(),
        });
      }
      setPendingEmailForSend('');
      await selectCandidatesMutation.mutateAsync({
        candidateIds: [activeCandidate.id],
        examConfig,
        sendNotification,
      });
      notify(
        feedbackForSendInterview({
          // Preserve "sent" copy until pipeline is loaded; only claim
          // automation-off when we know interviewing won't notify.
          willNotify:
            sendNotification &&
            (pipeline?.stages
              ? stageWillNotify(
                  findStageByType(pipeline.stages, 'interviewing')
                )
              : true),
        })
      );
      onStatusUpdate?.();
      return true;
    } catch (error) {
      console.error('Error sending interview:', error);
      notify(
        feedbackForSendInterview({
          httpError: feedbackErrorMessage(error),
        })
      );
      return false;
    } finally {
      setIsSendingInterview(false);
    }
  }, [
    activeCandidate,
    pendingEmailForSend,
    sendNotification,
    updateEmail,
    t,
    selectCandidatesMutation,
    examConfig,
    onStatusUpdate,
    pipeline?.stages,
  ]);

  const requestDelete = useCallback(
    (candidate: Candidate) => {
      onDeleteCandidate?.(candidate.id, candidate.talentName || 'Unknown');
    },
    [onDeleteCandidate]
  );

  const requestExtend = useCallback(
    (candidate: Candidate) => {
      if (onExtendInterview && candidate.interviewId) {
        onExtendInterview(
          candidate.interviewId,
          candidate.talentName || 'Unknown',
          candidate.interviewExpiresAt || null
        );
      }
    },
    [onExtendInterview]
  );

  /**
   * Kanban drop → confirmation shortcuts.
   *
   * Moves that apply straight away are handled optimistically by
   * `useCandidatePipelineDnd`; this only opens the dialog flows.
   */
  const requestMoveToStage = useCallback(
    (
      candidate: Candidate,
      targetStage: PipelineStage
    ) => {
      const mapped = mapDragToPipelineAction(candidate, {
        id: targetStage.id,
        stageType: targetStage.stageType,
        position: targetStage.position,
      });
      switch (mapped.kind) {
        case 'send_interview':
          openSendInterview(candidate);
          return true;
        case 'accept':
          openAccept(candidate);
          return true;
        case 'reject':
          openReject(candidate);
          return true;
        case 'hire':
          openHire(candidate);
          return true;
        default:
          return false;
      }
    },
    [openSendInterview, openAccept, openReject, openHire]
  );

  return {
    activeCandidate,
    isAccepting,
    isRejecting,
    isHiring,
    isSendingInterview,
    isUpdatingEmail,
    isSelectPending: selectCandidatesMutation.isPending,
    showSendInterviewDialog,
    pendingEmailForSend,
    setPendingEmailForSend,
    sendNotification,
    setSendNotification,
    showRejectDialog,
    setShowRejectDialog,
    showHireDialog,
    setShowHireDialog,
    showAcceptDialog,
    closeAccept,
    closeSendInterviewDialog,
    openSendInterview,
    openAccept,
    openReject,
    openHire,
    handleAccept,
    confirmReject,
    confirmHire,
    confirmSendInterview,
    requestDelete,
    requestExtend,
    requestMoveToStage,
    requiresReview,
  };
}

export type CandidatePipelineMutations = ReturnType<
  typeof useCandidatePipelineMutations
>;
